import { useId, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AnexoDoLancamento } from '@arena/shared/schemas';
import { financeiroApi } from '../api/financeiro.js';
import { Icone } from '../ui/Icone.js';
import { Botao } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { abrirComprovante, enviarComprovante, mensagemDoComprovante } from './anexos.js';
import { tamanhoDoArquivo } from './formatos.js';

/**
 * Os comprovantes de um lançamento: ver, anexar mais, tirar o errado.
 *
 * Abrir é para quem lê o razão — a URL é assinada e vive minutos. Anexar e
 * remover pedem `editar`, e é só isso que o `podeEditar` esconde: o servidor
 * decide de novo. Anexar e remover não mudam o fato (o valor, a data, o que
 * entrou no caixa), por isso valem em qualquer estado da linha — inclusive
 * no automático, onde o comprovante do Pix de uma fatura costuma chegar
 * depois.
 *
 * Remover é irreversível (a linha sai e o objeto sai do Storage), então
 * pede uma confirmação no próprio lugar — sem ela, um clique perdido
 * apagaria o comprovante.
 */
export function AnexosDoLancamento({
  lancamentoId,
  anexos,
  podeEditar,
}: {
  lancamentoId: string;
  anexos: readonly AnexoDoLancamento[];
  /** COSMÉTICO: esconde os botões; quem decide é o servidor. */
  podeEditar: boolean;
}) {
  const avisar = useToast();
  const cliente = useQueryClient();
  const titulo = useId();
  const entrada = useRef<HTMLInputElement>(null);

  const [abrindo, setAbrindo] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  // O detalhe, o extrato e o resumo moram sob ['financeiro']: um comprovante
  // novo não muda número nenhum, mas o detalhe precisa refazer a lista.
  const invalidar = () => cliente.invalidateQueries({ queryKey: ['financeiro'] });
  const enviar = useMutation({
    mutationFn: (arquivo: File) => enviarComprovante(lancamentoId, arquivo),
    onSuccess: invalidar,
  });
  const remover = useMutation({
    mutationFn: (anexoId: string) => financeiroApi.excluirAnexo(anexoId),
    onSuccess: invalidar,
  });

  async function anexar(arquivo: File) {
    setErro(null);
    try {
      await enviar.mutateAsync(arquivo);
      avisar('Comprovante anexado.');
    } catch (e) {
      setErro(mensagemDoComprovante(e));
    }
  }

  async function abrir(anexo: AnexoDoLancamento) {
    setErro(null);
    setAbrindo(anexo.id);
    try {
      await abrirComprovante(anexo.id);
    } catch (e) {
      setErro(mensagemDoComprovante(e));
    } finally {
      setAbrindo(null);
    }
  }

  async function tirar(anexo: AnexoDoLancamento) {
    setErro(null);
    try {
      await remover.mutateAsync(anexo.id);
      setConfirmando(null);
      avisar('Comprovante removido.');
    } catch (e) {
      setErro(mensagemDoComprovante(e));
    }
  }

  return (
    <section aria-labelledby={titulo} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div id={titulo} style={{ fontSize: 13, fontWeight: 700 }}>
        Comprovantes
      </div>

      {anexos.length === 0 ? (
        <p className="sub">Nenhum comprovante anexado.</p>
      ) : (
        <ul className="feed">
          {anexos.map((anexo) => (
            <li key={anexo.id}>
              <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                <b>{anexo.nome}</b>
                <small>{tamanhoDoArquivo(anexo.tamanho)}</small>
              </div>
              <div className="rt" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {confirmando === anexo.id ? (
                  <>
                    <Botao variante="fantasma" pequeno onClick={() => setConfirmando(null)}>
                      Manter
                    </Botao>
                    <Botao
                      variante="contorno"
                      pequeno
                      onClick={() => void tirar(anexo)}
                      disabled={remover.isPending}
                    >
                      {remover.isPending ? 'Removendo…' : 'Remover de vez'}
                    </Botao>
                  </>
                ) : (
                  <>
                    <Botao
                      variante="contorno"
                      pequeno
                      onClick={() => void abrir(anexo)}
                      disabled={abrindo === anexo.id}
                    >
                      {abrindo === anexo.id ? 'Abrindo…' : 'Abrir'}
                    </Botao>
                    {podeEditar && (
                      <Botao variante="fantasma" pequeno onClick={() => setConfirmando(anexo.id)}>
                        Remover
                      </Botao>
                    )}
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {podeEditar && (
        <div>
          <Botao
            variante="contorno"
            pequeno
            icone="clip"
            onClick={() => entrada.current?.click()}
            disabled={enviar.isPending}
          >
            {enviar.isPending ? 'Enviando…' : 'Anexar comprovante'}
          </Botao>
          {/* O seletor de arquivo é do navegador; o botão acima o abre. `hidden`
              tira o campo da ordem de tabulação — o botão é quem recebe o foco. */}
          <input
            ref={entrada}
            type="file"
            hidden
            accept="application/pdf,image/jpeg,image/png,image/webp"
            onChange={(e) => {
              const arquivo = e.target.files?.[0];
              // Zera para que o MESMO arquivo, depois de uma falha, possa ser escolhido de novo.
              e.target.value = '';
              if (arquivo) void anexar(arquivo);
            }}
          />
          <div className="help" style={{ marginTop: 6, fontSize: 11.5, color: 'var(--dim)' }}>
            PDF, JPG, PNG ou WebP · até 10 MB.
          </div>
        </div>
      )}

      {erro && (
        <div className="aviso conflito" role="alert">
          <Icone nome="info" />
          <div>{erro}</div>
        </div>
      )}
    </section>
  );
}
